import React, { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button, Switch, Empty, Modal, message } from 'antd';
import { toUserMessages } from '../../apiService/blustreamApi';
import { deriveModel } from './deriveTree';
import { useJourneyDetailQuery } from './data/queries';
import { useTouchpointEditor } from './data/useTouchpointEditor';
import { useJourneyEditor } from './data/useJourneyEditor';
import { applyPreview } from './previewOrder';
import { applyJourneyPreview } from './data/applyJourneyPreview';
import { useProductOptions, useDeleteJourney } from './data/journeyHooks';
import { useDeleteTouchpoint } from './data/touchpointHooks';
import { buildProductById } from './segment/segmentRead';
import { branchJourneySeed } from './branch';
import TimelineCanvas from './TimelineCanvas';
import TouchpointEditorDrawer from './TouchpointEditorDrawer';
import JourneyEditorDrawer from './JourneyEditorDrawer';
import DeleteConfirmModal from '../../Components/Common/DeleteConfirmModal';
import ReadOnlyNotice from './ReadOnlyNotice';
import './timeline.css';

const PILL = { LIVE: 'live', PAUSED: 'paused', MUTED: 'paused', DISABLED: 'off', ARCHIVED: 'off' };

export default function JourneyTimeline() {
  const { journeyId } = useParams();
  const history = useHistory();
  const { data, isLoading: loading, isError: error } = useJourneyDetailQuery(journeyId);
  const [showRelated, setShowRelated] = useState(true);
  const [showMetrics, setShowMetrics] = useState(true);
  const [target, setTarget] = useState(null);      // { mode, touchpoint, journeyId, siblings }
  const [journeyTarget, setJourneyTarget] = useState(null); // { mode, journey }

  // single source of truth for the editor's pending state (lifted here so the
  // controlled drawer AND the live preview both read from it)
  const editor = useTouchpointEditor({
    open: !!target,
    mode: target ? target.mode : 'edit',
    touchpoint: target ? target.touchpoint : null,
    siblings: target ? target.siblings : [],
    journeyId: target ? target.journeyId : journeyId,
    afterTouchpointId: target ? target.afterTouchpointId : null,
  });

  const journeyEditor = useJourneyEditor({
    open: !!journeyTarget,
    mode: journeyTarget ? journeyTarget.mode : 'configure',
    journey: journeyTarget ? journeyTarget.journey : null,
    journeyId: journeyTarget && journeyTarget.mode !== 'create' ? journeyTarget.journey.journey_id : null,
  });

  const deleteJourney = useDeleteJourney();
  const deleteTouchpoint = useDeleteTouchpoint();
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind:'journey', journey } | { kind:'touchpoint', touchpoint }

  // product-name resolver for the card's segment chips (parity with the drawer summary);
  // empty (e.g. unauthenticated /preview) → chips fall back to the product id.
  const { data: productOptions = [] } = useProductOptions();
  const productById = buildProductById(productOptions);

  const live = data;
  if (loading || !live) return <div className="tl-page" style={{ padding: 40 }}>Loading…</div>;
  if (error) return <div className="tl-page" style={{ padding: 40 }}><Empty description="Couldn't load journeys." /></div>;

  // Surface any save/API failure instead of failing silently (a drawer that just
  // won't save with no feedback). Shared by the touchpoint + journey editors.
  const showErr = (err) => message.error(toUserMessages(err).join(' '));

  const siblingsOf = (jid) => (live.journeys.find((j) => j.journey_id === jid)?.touchpoints || [])
    .map((t) => ({ touchpoint_id: t.touchpoint_id, ordinal: t.ordinal }));

  // segment lookup helper for seeding the journey editor's segment draft
  const segById = (sid) => (live.segments || []).find((s) => s.segment_id === sid) || null;

  // while either editor is open, other cards' pencils and + are inert (no switching or stacking drawers)
  const onEditTp = (tp) => { if (target || journeyTarget) return; setTarget({ mode: 'edit', touchpoint: tp, journeyId: tp.journey_id, siblings: siblingsOf(tp.journey_id) }); };
  const onAddAfter = (jid, afterTpId) => { if (target || journeyTarget) return; setTarget({ mode: 'create', touchpoint: null, journeyId: jid, siblings: siblingsOf(jid), afterTouchpointId: afterTpId }); };
  // Branching opens the journey editor in create mode, seeded with a segment keyed on the
  // trigger's response attribute; saving creates the segment + journey via the API.
  const onAddBranch = (tpId) => {
    if (target || journeyTarget) return;
    const tp = live.journeys.flatMap((j) => j.touchpoints).find((t) => t.touchpoint_id === tpId);
    if (!tp) return;
    if (tp.response_attribute_id == null) {
      message.info('Set a response attribute on this touchpoint before adding a branch.');
      onEditTp(tp);
      return;
    }
    setJourneyTarget({ mode: 'create', journey: branchJourneySeed(tp) });
  };
  // `j` is the DERIVED model journey (deriveTree: { journeyId, name, segment, … }) — it lacks
  // journey_id / segment_id / config fields. Seed the editor from the RAW journey in live.journeys
  // (full API shape) instead, or state/channels/labels/segment all seed as defaults/null.
  const onEditJourney = (j) => {
    const raw = live.journeys.find((x) => x.journey_id === j.journeyId);
    if (!raw) return;
    setJourneyTarget({ mode: 'configure', journey: { ...raw, segment: segById(raw.segment_id) } });
  };

  // Delete is triggered from inside the editor drawers (footer Delete button), so the
  // relevant editor is already open when these fire. They open the type-to-confirm modal
  // over the drawer; confirming closes both. journeyTarget.journey is the RAW journey
  // (journey_id/name); target.touchpoint is the raw touchpoint.
  const onDeleteJourneyRequest = () => { if (journeyTarget) setDeleteTarget({ kind: 'journey', journey: journeyTarget.journey }); };
  const onDeleteTouchpointRequest = () => { if (target && target.touchpoint) setDeleteTarget({ kind: 'touchpoint', touchpoint: target.touchpoint }); };
  const confirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.kind === 'journey') {
      const jid = deleteTarget.journey.journey_id;
      deleteJourney.mutate(jid, {
        onSuccess: () => {
          setDeleteTarget(null);
          setJourneyTarget(null); // close the journey editor drawer
          if (jid === Number(journeyId)) {
            history.push(history.location.pathname.startsWith('/preview') ? '/preview/timeline' : '/journeys');
          }
        },
        onError: showErr,
      });
    } else {
      const tp = deleteTarget.touchpoint;
      deleteTouchpoint.mutate({ journeyId: tp.journey_id, touchpointId: tp.touchpoint_id }, {
        onSuccess: () => { setDeleteTarget(null); setTarget(null); }, // close the touchpoint editor drawer
        onError: showErr,
      });
    }
  };

  const requestClose = () => {
    if (target && editor.dirty) {
      Modal.confirm({
        title: 'Discard changes?',
        content: 'Your unsaved changes to this touchpoint will be lost.',
        okText: 'Discard', okType: 'danger', cancelText: 'Keep editing',
        onOk: () => setTarget(null),
      });
    } else {
      setTarget(null);
    }
  };

  const journey = live.journeys.find((j) => j.journey_id === Number(journeyId));
  if (!journey) {
    return (
      <div className="tl-page" style={{ padding: 40 }}>
        <Empty description="Journey not found." />
        <Button type="primary" onClick={() => history.push(history.location.pathname.startsWith('/preview') ? '/preview/timeline' : '/journeys')}>Back to journeys</Button>
      </div>
    );
  }

  // apply the live preview overlays before deriving the model:
  // 1. touchpoint preview: edit replaces+moves the real touchpoint; create splices in a phantom card.
  // 2. journey preview: overlays name/state/channels from the journey editor draft.
  const tpPreviewJourneys = (target && editor.previewTouchpoint)
    ? applyPreview(live.journeys, target.journeyId, editor.previewTouchpoint, editor.targetIndex, editor.isCreate)
    : live.journeys;
  const previewJourneys = applyJourneyPreview(tpPreviewJourneys, journeyEditor.previewJourney);
  const model = deriveModel(journey.journey_id, { journeys: previewJourneys, segments: live.segments });

  return (
    <div className="tl-page">
      <div className="tl-header">
        <span className="tl-jname">{journey.name}<ReadOnlyNotice /></span>
        <span className={`tl-pill ${PILL[journey.state_enum] || 'off'}`}>● {journey.state_enum.charAt(0) + journey.state_enum.slice(1).toLowerCase()}</span>
        <span className="tl-spacer" />
        <span>Show Related Journeys</span>
        <Switch checked={showRelated} aria-label="toggle related journeys" onChange={setShowRelated} />
        <span>Show Metrics</span>
        <Switch checked={showMetrics} aria-label="toggle metrics" onChange={setShowMetrics} />
      </div>
      <TimelineCanvas model={model} showRelated={showRelated} showMetrics={showMetrics}
        attributeById={live.attributeById}
        productById={productById}
        focusTouchpointId={target && editor.previewTouchpoint ? editor.previewTouchpoint.touchpoint_id : null}
        focusJourneyId={journeyTarget && journeyTarget.mode !== 'create' ? journeyTarget.journey.journey_id : null}
        addsLocked={!!target || !!journeyTarget}
        rightInset={target ? 440 : (journeyTarget ? 460 : 0)}
        onEdit={onEditTp}
        onEditJourney={onEditJourney}
        onAddAfter={onAddAfter} onAddBranch={onAddBranch} />
      <TouchpointEditorDrawer
        open={!!target}
        editor={editor}
        onRequestClose={requestClose}
        onSave={() => editor.save(() => setTarget(null), showErr)}
        onDelete={onDeleteTouchpointRequest} />
      <JourneyEditorDrawer
        open={!!journeyTarget}
        editor={journeyEditor}
        onRequestClose={() => {
          if (journeyEditor.dirty) {
            Modal.confirm({
              title: 'Discard changes?',
              content: 'Your unsaved changes to this journey will be lost.',
              okText: 'Discard', okType: 'danger', cancelText: 'Keep editing',
              onOk: () => setJourneyTarget(null),
            });
          } else {
            setJourneyTarget(null);
          }
        }}
        onSave={() => journeyEditor.save(() => setJourneyTarget(null), showErr)}
        onDelete={onDeleteJourneyRequest}
        attributeById={live.attributeById} />
      <DeleteConfirmModal
        open={!!deleteTarget}
        title={deleteTarget && deleteTarget.kind === 'journey' ? 'Delete journey' : 'Delete touchpoint'}
        nameToType={(deleteTarget && (deleteTarget.kind === 'journey' ? deleteTarget.journey.name : deleteTarget.touchpoint.name)) || 'DELETE'}
        body={deleteTarget && (deleteTarget.kind === 'journey'
          ? <>This permanently deletes the journey <b>{deleteTarget.journey.name}</b> and its touchpoints. This can’t be undone. Type the name to confirm.</>
          : <>This permanently deletes the touchpoint <b>{deleteTarget.touchpoint.name}</b>. This can’t be undone. Type the name to confirm.</>)}
        confirming={deleteJourney.isLoading || deleteTouchpoint.isLoading}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
